import { memo } from 'react';
import { ToolName } from '../../../lib/constants';
import { cn } from '../../../lib/utils';
import { TextLoop } from './TextLoop';

interface ToolLoadingIndicatorProps {
  toolName: string;
  className?: string;
}

const ToolLoadingIndicatorComponent = ({
  toolName,
  className,
}: ToolLoadingIndicatorProps) => {
  if (!toolName || toolName === 'streaming') {
    return (
      <div
        className={cn(
          'bitte-bg-secondary bitte-rounded-lg bitte-p-4',
          className,
        )}
      >
        <div className="bitte-flex bitte-items-center bitte-gap-4">
          <div className="bitte-w-4 bitte-h-4 bitte-border-2 bitte-border-gray-400 bitte-border-t-transparent bitte-rounded-full bitte-animate-spin" />
          <span className="bitte-text-secondary-foreground bitte-font-medium">
            Processing your request...
          </span>
        </div>
      </div>
    );
  }

  const renderToolLoading = () => {
    switch (toolName) {
      case ToolName.GENERATE_EVM_TX:
        return (
          <TextLoop
            interval={1.5}
            className="bitte-text-secondary-foreground bitte-font-medium"
          >
            <span>Generating your transaction...</span>
            <span>Building EVM transaction...</span>
            <span>Almost ready...</span>
          </TextLoop>
        );

      case ToolName.GENERATE_TRANSACTION:
        return (
          <TextLoop
            interval={1.5}
            className="bitte-text-secondary-foreground bitte-font-medium"
          >
            <span>Preparing your transaction...</span>
            <span>Building transaction data...</span>
            <span>Almost done...</span>
          </TextLoop>
        );

      case ToolName.GENERATE_SUI_TX:
        return (
          <TextLoop
            interval={1.5}
            className="bitte-text-secondary-foreground bitte-font-medium"
          >
            <span>Creating your Sui transaction...</span>
            <span>Building transaction...</span>
            <span>Getting things ready...</span>
          </TextLoop>
        );

      case ToolName.GENERATE_CARDANO_TX:
        return (
          <TextLoop
            interval={1.5}
            className="bitte-text-secondary-foreground bitte-font-medium"
          >
            <span>Building your Cardano transaction...</span>
            <span>Preparing transaction data...</span>
            <span>Almost ready...</span>
          </TextLoop>
        );

      case ToolName.GENERATE_SOL_TX:
        return (
          <TextLoop
            interval={1.5}
            className="bitte-text-secondary-foreground bitte-font-medium"
          >
            <span>Building your Solana transaction...</span>
            <span>Preparing transaction data...</span>
            <span>Almost ready...</span>
          </TextLoop>
        );

      case ToolName.BUILD_SWAP_TX:
        return (
          <TextLoop
            interval={1.5}
            className="bitte-text-secondary-foreground bitte-font-medium"
          >
            <span>Building swap transaction...</span>
            <span>Preparing Cardano tx...</span>
            <span>Almost ready...</span>
          </TextLoop>
        );

      case ToolName.GET_SWAP_ESTIMATE_QUERY:
        return (
          <TextLoop
            interval={1.5}
            className="bitte-text-secondary-foreground bitte-font-medium"
          >
            <span>Getting swap quote...</span>
            <span>Calculating rates...</span>
            <span>Finding best price...</span>
          </TextLoop>
        );

      case ToolName.SWAP:
        return (
          <TextLoop
            interval={1.5}
            className="bitte-text-secondary-foreground bitte-font-medium"
          >
            <span>Preparing your swap...</span>
            <span>Finding best rates...</span>
            <span>Almost ready...</span>
          </TextLoop>
        );

      case ToolName.INTENTS:
        return (
          <TextLoop
            interval={1.5}
            className="bitte-text-secondary-foreground bitte-font-medium"
          >
            <span>Processing your intent...</span>
            <span>Analyzing request...</span>
            <span>Preparing response...</span>
          </TextLoop>
        );

      case ToolName.GETQUOTE:
        return (
          <TextLoop
            interval={1.5}
            className="bitte-text-secondary-foreground bitte-font-medium"
          >
            <span>Getting your quote...</span>
            <span>Analyzing market prices...</span>
            <span>Calculating best rates...</span>
            <span>Finalizing quote...</span>
          </TextLoop>
        );

      case ToolName.CREATE_ORDER:
        return (
          <TextLoop
            interval={1.5}
            className="bitte-text-secondary-foreground bitte-font-medium"
          >
            <span>Creating your order...</span>
            <span>Processing order details...</span>
            <span>Submitting to exchange...</span>
            <span>Almost ready...</span>
          </TextLoop>
        );

      case ToolName.GET_PORTFOLIO:
        return (
          <TextLoop
            interval={1.5}
            className="bitte-text-secondary-foreground bitte-font-medium"
          >
            <span>Fetching your portfolio...</span>
            <span>Loading asset data...</span>
            <span>Almost ready...</span>
          </TextLoop>
        );

      case ToolName.GET_BALANCE:
        return (
          <TextLoop
            interval={1.5}
            className="bitte-text-secondary-foreground bitte-font-medium"
          >
            <span>Checking your balance...</span>
            <span>Loading account data...</span>
            <span>Almost ready...</span>
          </TextLoop>
        );

      case ToolName.SEARCH:
        return (
          <TextLoop
            interval={1.5}
            className="bitte-text-secondary-foreground bitte-font-medium"
          >
            <span>Searching...</span>
            <span>Analyzing results...</span>
            <span>Preparing data...</span>
          </TextLoop>
        );

      case ToolName.ANALYZE:
        return (
          <TextLoop
            interval={1.5}
            className="bitte-text-secondary-foreground bitte-font-medium"
          >
            <span>Analyzing data...</span>
            <span>Processing information...</span>
            <span>Almost done...</span>
          </TextLoop>
        );

      default:
        // Fallback for unknown tools
        return (
          <span className="bitte-text-secondary-foreground bitte-font-medium">
            Processing your request...
          </span>
        );
    }
  };

  return (
    <div
      className={cn('bitte-bg-secondary bitte-rounded-lg bitte-p-4', className)}
    >
      <div className="bitte-flex bitte-items-center bitte-gap-4">
        <div className="bitte-w-4 bitte-h-4 bitte-border-2 bitte-border-gray-400 bitte-border-t-transparent bitte-rounded-full bitte-animate-spin" />
        {renderToolLoading()}
      </div>
    </div>
  );
};

export const ToolLoadingIndicator = memo(ToolLoadingIndicatorComponent);
